import { ArrowDown, ArrowUp } from "@dreamhub/designsystem/icons";
import HeaderAction from "@/components/controls/DataTable/TableHeader/HeaderAction";
import HeaderFilterPopover from "@/components/controls/DataTable/TableHeader/HeaderFilterPopover";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import { cn } from "@/utils/utils";
import { flexRender, Header } from "@tanstack/react-table";
import { GripVertical } from "lucide-react";
import { FC } from "react";
import { useTranslation } from "react-i18next";

interface HeaderContentProps {
  header: Header<any, unknown>;
  isSortable: boolean;
  sortDirection: false | "asc" | "desc";
  isHovered: boolean;
  showDragHandle?: boolean;
  dragHandleProps?: any;
  filterOptions?: DataTableFilter[];
  filters?: Filters;
  setFilters?: FiltersSetter;
}

const HeaderContent: FC<HeaderContentProps> = ({
  header,
  isSortable,
  sortDirection,
  isHovered,
  showDragHandle,
  dragHandleProps,
  filterOptions = [],
  filters = {},
  setFilters
}) => {
  const { t } = useTranslation();
  const meta = header.column.columnDef.meta as any;
  const textAlign = meta?.textAlign ?? "start";
  const filterKey = meta?.filterKey ?? header.column.id;

  const columnHeader = header.column.columnDef.header;
  const columnName =
    typeof columnHeader === "string" ? columnHeader : header.column.id;

  const hasFilterOption = filterOptions.some(
    (option) => option.key === filterKey
  );
  const showFilter = hasFilterOption && setFilters;

  return (
    <div
      className={cn(
        "flex flex-row items-center w-full h-full min-h-[3rem]",
        showDragHandle ? "group" : ""
      )}
    >
      {showDragHandle && (
        <div
          {...dragHandleProps}
          className={cn(
            "px-0 cursor-grab active:cursor-grabbing",
            "opacity-0 group-hover:opacity-100 transition-opacity"
          )}
        >
          <GripVertical className="h-4 w-4 text-secondary" />
        </div>
      )}
      <div
        onClick={header.column.getToggleSortingHandler()}
        className={cn(
          showDragHandle
            ? "flex-1 flex items-center"
            : "flex flex-row items-center w-full",
          `justify-${textAlign}`,
          `text-${textAlign}`,
          "text-pretty overflow-hidden",
          "w-full justify-between px-1"
        )}
      >
        <div
          className={cn(
            "line-clamp-3 text-pretty break-words font-normal",
            `text-${textAlign === "start" ? "start" : "center"}`
          )}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
        <div className="flex items-end">
          {showFilter && (
            <HeaderFilterPopover
              filterKey={filterKey}
              filterOptions={filterOptions}
              filters={filters}
              setFilters={setFilters}
              isHovered={isHovered}
            />
          )}
          {isSortable && !sortDirection && (
            <HeaderAction
              icon={<ArrowUp />}
              className={
                !sortDirection && !isHovered ? "opacity-0" : "opacity-100"
              }
              tooltipText={t("datatoolbar.display.sortByColumn", {
                columnName
              })}
            />
          )}
          {sortDirection &&
            (sortDirection === "desc" ? (
              <HeaderAction
                icon={<ArrowDown />}
                iconClassName="!text-secondary"
              />
            ) : (
              <HeaderAction
                icon={<ArrowUp />}
                iconClassName="!text-secondary"
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default HeaderContent;
